//from https://github.com/samhirtarif/react-audio-recorder/blob/master/src/hooks/useAudioRecorder.ts
import { useState, useCallback } from 'react'
import { AddErrorAlert } from '../../slices/alert'
import { useAppDispatch } from '../redux'

export interface recorderControls {
  startRecording: () => void
  stopRecording: () => void
  togglePauseResume: () => void
  recordingBlob?: Blob
  isRecording: boolean
  isPaused: boolean
  recordingTime: number
  clearRecord: () => void
}

/**
 * @returns Controls for the recording. Details of returned controls are given below
 *
 * @details `startRecording`: Calling this method would result in the recording to start. Sets `isRecording` to true
 * @details `stopRecording`: This results in a recording in progress being stopped and the resulting audio being present in `recordingBlob`. Sets `isRecording` to false
 * @details `togglePauseResume`: Calling this method would pause the recording if it is currently running or resume if it is paused. Toggles the value `isPaused`
 * @details `recordingBlob`: This is the recording blob that is created after `stopRecording` has been called
 * @details `isRecording`: A boolean value that represents whether a recording is currently in progress
 * @details `isPaused`: A boolean value that represents whether a recording in progress is paused
 * @details `recordingTime`: Number of seconds that the recording has gone on. This is updated every second
 */
const useAudioRecorder: () => recorderControls = () => {
  const dispatch = useAppDispatch()
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>()
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timer>()
  const [recordingBlob, setRecordingBlob] = useState<Blob>()
  const _startTimer: () => void = () => {
    const interval = setInterval(() => {
      setRecordingTime(time => time + 1)
    }, 1000)
    setTimerInterval(interval)
  }

  const _stopTimer: () => void = () => {
    // @ts-ignore
    timerInterval != null && clearInterval(timerInterval)
    setTimerInterval(undefined)
  }

  /**
   * Calling this method would result in the recording to start. Sets `isRecording` to true
   */
  const startRecording: () => void = useCallback(() => {
    if (timerInterval != null) return

    if ('MediaRecorder' in window) {
      navigator.mediaDevices
        .getUserMedia({ audio: true, video: false })
        .then(stream => {
          setIsRecording(true)
          const recorder: MediaRecorder = new MediaRecorder(stream)
          setMediaRecorder(recorder)
          recorder.start()
          _startTimer()

          recorder.addEventListener('dataavailable', event => {
            const needsCancel = localStorage.getItem('cancel-save')
            if (needsCancel !== 'true') {
              if (typeof event.data === 'undefined') return
              if (event.data.size === 0) return
              setRecordingBlob(event.data)
            } else {
              localStorage.removeItem('cancel-save')
            }
            recorder.stream.getTracks().forEach(t => t.stop())
            setMediaRecorder(null)
          })
        })
        .catch(err => {
          setIsRecording(false)
          setMediaRecorder(null)
          console.error('Error getting user media stream', err)
          dispatch(AddErrorAlert({ err }))
        })
    } else {
      dispatch(
        AddErrorAlert({
          err: {
            message: '"The MediaRecorder API is not supported in your browser."'
          }
        })
      )
    }
  }, [timerInterval, dispatch])

  /**
   * Calling this method results in a recording in progress being stopped and the resulting audio being present in `recordingBlob`. Sets `isRecording` to false
   */
  const stopRecording: () => void = () => {
    mediaRecorder?.stop()
    _stopTimer()
    setRecordingTime(0)
    setIsRecording(false)
    setIsPaused(false)
  }

  /**
   * Calling this method would pause the recording if it is currently running or resume if it is paused. Toggles the value `isPaused`
   */
  const togglePauseResume: () => void = () => {
    if (isPaused) {
      setIsPaused(false)
      mediaRecorder?.resume()
      _startTimer()
    } else {
      setIsPaused(true)
      _stopTimer()
      mediaRecorder?.pause()
    }
  }

  const clearRecord = useCallback(() => {
    setRecordingBlob(undefined)
  }, [setRecordingBlob])

  return {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
    clearRecord
  }
}

export default useAudioRecorder
